import { QualificationEnum } from "@/helpers/enums";
import { generateSlug } from "@/helpers/generateSlug";
import type { Messages } from "@/messages/buildCatalog";
import { deCatalog } from "@/messages/buildCatalog";

export interface TeamMemberRecognition {
  date: string;
  title: string;
  url: string;
  imageUrl: string;
  imageAlt: string;
}

export interface TeamMember {
  slug: string;
  fullName: string;
  prename: string;
  role: string;
  image: {
    1050: string;
    600: string;
  };
  imageAlt: string;

  email: string;
  phone: string;
  bio: string;
  expertise: string[];
  qualifications: {
    [QualificationEnum.Education]: string[];
    [QualificationEnum.ProfessionalQualifications]: string[];
    [QualificationEnum.ProfessionalMemberships]: string[];
    [QualificationEnum.AdditionalResponsibilities]: string[];
    [QualificationEnum.Hobbies]: string[];
  };
  quote: {
    text: string;
    author: string;
  };
  recognitions?: TeamMemberRecognition[];
}

export enum TeamMemberEnum {
  MoritzRiehl = "moritzRiehl",
  HubertusScherbarth = "hubertusScherbarth",
}

/** Contact details and image paths — same for every locale. */
const TEAM_MEMBER_CONTACT: Record<
  TeamMemberEnum,
  Pick<TeamMember, "image" | "email" | "phone">
> = {
  [TeamMemberEnum.MoritzRiehl]: {
    image: {
      600: "/images/persons/moritz-riehl-600.jpg",
      1050: "/images/persons/moritz-riehl-1050.jpg",
    },
    email: "moritz.riehl@solving.legal",
    phone: "+4963416817171",
  },
  [TeamMemberEnum.HubertusScherbarth]: {
    image: {
      600: "/images/persons/hubertus-scherbarth-600.jpg",
      1050: "/images/persons/hubertus-scherbarth-1050.jpg",
    },
    email: "hubertus.scherbarth@solving.legal",
    phone: "+4971125259890",
  },
};

/** Slugs are derived from German legal names so URLs stay stable across locales. */
export function getTeamMemberSlug(member: TeamMemberEnum): string {
  return generateSlug(deCatalog.team.persons[member].fullName);
}

export function getTeamMemberData(
  member: TeamMemberEnum,
  team: Messages["team"],
): TeamMember {
  const p = team.persons[member];
  const recognitions =
    "recognitions" in p &&
    Array.isArray(p.recognitions) &&
    p.recognitions.length > 0
      ? p.recognitions.map((r) => ({ ...r }))
      : undefined;
  return {
    slug: getTeamMemberSlug(member),
    fullName: p.fullName,
    prename: p.prename,
    role: p.role,
    expertise: [...p.expertise],
    image: TEAM_MEMBER_CONTACT[member].image,
    imageAlt: p.imageAlt,
    email: TEAM_MEMBER_CONTACT[member].email,
    phone: TEAM_MEMBER_CONTACT[member].phone,
    bio: p.bio,
    qualifications: {
      [QualificationEnum.Education]: [
        ...p.qualifications[QualificationEnum.Education],
      ],
      [QualificationEnum.ProfessionalQualifications]: [
        ...p.qualifications[QualificationEnum.ProfessionalQualifications],
      ],
      [QualificationEnum.ProfessionalMemberships]: [
        ...p.qualifications[QualificationEnum.ProfessionalMemberships],
      ],
      [QualificationEnum.AdditionalResponsibilities]: [
        ...p.qualifications[QualificationEnum.AdditionalResponsibilities],
      ],
      [QualificationEnum.Hobbies]: [
        ...p.qualifications[QualificationEnum.Hobbies],
      ],
    },
    quote: {
      text: p.quote.text,
      author: p.quote.author,
    },
    ...(recognitions ? { recognitions } : {}),
  };
}

export function getTeamMembersRecord(
  team: Messages["team"],
): Record<TeamMemberEnum, TeamMember> {
  return {
    [TeamMemberEnum.MoritzRiehl]: getTeamMemberData(
      TeamMemberEnum.MoritzRiehl,
      team,
    ),
    [TeamMemberEnum.HubertusScherbarth]: getTeamMemberData(
      TeamMemberEnum.HubertusScherbarth,
      team,
    ),
  };
}

/** German copy; use `getTeamMembersRecord(getMessages(locale).team)` for UI. */
export const teamMembers = getTeamMembersRecord(deCatalog.team);

export function getTeamMemberBySlug(
  slug: string,
  team: Messages["team"] = deCatalog.team,
): TeamMember | undefined {
  return Object.values(getTeamMembersRecord(team)).find((m) => m.slug === slug);
}
